import { UseQueryOptions, UseQueryResult } from 'react-query/types/react/types';
import { useAPIs } from 'src/hooks/useAPIs';
import { useQuery } from 'react-query';
import { QueryKey } from 'src/models/query';
import { Materials_Filter, Videos_Filter } from 'src/graphql/schema.graphql';
import { PartialDeep } from 'type-fest';
import { NeauviaItem } from 'src/models/directus';
import { materialsBasicGqlQuery } from 'src/modules/materials/graphql/materials-basic.query';
import { videosBasicGqlQuery } from 'src/modules/videos/graphql/videos-basic.query';
import { omit, pick } from 'rambda';
import { parseISO } from 'date-fns';
import { APIs } from 'src/models/apis';
import {
  SearchItemsToQuery,
  getItemSpecificFilterKeys,
  commonFiltersToQueryMap,
  searchFiltersToQueryMap,
  hiddenFilters,
} from 'src/services/search';
import { GqlFragment } from 'src/utils/gql/query';

export type NeauviaItemQueryFilters =
  | PartialDeep<Videos_Filter>
  | PartialDeep<Materials_Filter>;

export type NeauviaItemQueryVariables = {
  filters?: NeauviaItemQueryFilters;
  sort?: string[];
  limit?: number;
  itemsToQuery?: SearchItemsToQuery[];
};

const queriesMap: Record<SearchItemsToQuery, GqlFragment> = {
  videos: videosBasicGqlQuery,
  materials: materialsBasicGqlQuery,
};

export const fetchNeauviaItems = async (
  apis: APIs,
  variables?: NeauviaItemQueryVariables,
) => {
  let itemsToQuery = variables?.itemsToQuery ?? ['materials', 'videos'];

  /**
   * Check if passed filters include filter parameter specific for any item kind.
   * If so, don't fetch other items - e.g "duration" filter was passed, then don't fetch any materials, just videos.
   */
  if (variables?.filters) {
    const itemToQuery = itemsToQuery.find((kind) =>
      Object.keys(variables?.filters ?? {}).some((key) =>
        Object.keys(
          omit(
            Object.keys(commonFiltersToQueryMap),
            omit(hiddenFilters, searchFiltersToQueryMap[kind]),
          ),
        ).includes(key),
      ),
    );

    if (itemToQuery) {
      itemsToQuery = [itemToQuery];
    }

    // Workaround to the comment above :(
    if (
      'type' in variables.filters &&
      variables.filters.type?._in?.includes('videos')
    ) {
      itemsToQuery.push('videos');
    }
  }

  const directus = await apis.directus();

  const result = await itemsToQuery.reduce<Promise<NeauviaItem[]>>(
    async (prev, curr) => {
      const options = {
        ...variables,
        filters: pick(
          ['_or', ...getItemSpecificFilterKeys(curr)],
          variables?.filters,
        ),
      };

      const items = await directus
        .query<Record<SearchItemsToQuery, NeauviaItem[]>>(
          queriesMap[curr],
          options,
        )
        .then((d) => d[curr]);

      return [...(await prev), ...items];
    },
    Promise.resolve([]),
  );

  return result
    .sort(
      (a, b) =>
        parseISO(b.date_created).getTime() - parseISO(a.date_created).getTime(),
    )
    .slice(0, variables?.limit);
};

export const useNeauviaItemsListQuery = (
  variables?: NeauviaItemQueryVariables,
  options?: Omit<UseQueryOptions<NeauviaItem[]>, 'queryKey' | 'queryFn'>,
): UseQueryResult<NeauviaItem[]> => {
  const apis = useAPIs();
  return useQuery(
    [...QueryKey.MaterialsList, variables],
    () => fetchNeauviaItems(apis, variables),
    options,
  );
};
